import { z } from "zod";
import { postHistory } from "./history";
import { effectiveBlogRoutes } from "./runtime";
import { blogModule, freeRedirect, moveRedirect } from "./redirects";
import { BlogStore, postFromRow, settingsFromJson } from "./store";
import { samePostCopy } from "./state";
import { BLOG_UNAVAILABLE, hasFeature, loadSiteFeatures } from "../../features";
/** A refusal the owner can read: an address already taken, a post that isn't ready, nothing to undo. */
export class BlogRefusal extends Error {
    name = "BlogRefusal";
}
/** The post changed since the caller last read it (`row_version`); it should reload and try again. */
export class BlogConflict extends Error {
    name = "BlogConflict";
}
/** The platform hasn't turned the blog on for this site (`features.ts`). Answered as 403, not as a rule the owner can fix. */
export class BlogUnavailable extends Error {
    name = "BlogUnavailable";
    constructor() { super(BLOG_UNAVAILABLE); }
}
const identifier = z.string().regex(/^[a-z][a-z0-9_-]{0,63}$/i);
const slug = z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/);
const base = z.string().regex(/^\/[a-z0-9]+(?:-[a-z0-9]+)*$/).max(200);
export const PostCopySchema = z.object({
    title: z.string().trim().min(1).max(160), slug: slug.max(120), summary: z.string().max(500).optional(),
    cover: z.object({ assetId: identifier, version: z.number().int().positive(), alt: z.string().max(500) }).strict().optional(),
    tags: z.array(identifier).max(16), revision: z.string().regex(/^[a-f0-9]{64}$/), publishAt: z.string().datetime({ offset: true }).optional(),
}).strict();
export const BlogSettingsInputSchema = z.object({ name: z.string().trim().min(1).max(80), base, enabled: z.boolean(), addToMenu: z.boolean().optional(), latestOnHome: z.boolean().optional() }).strict();
/** A JSON column's parameter: SQL NULL for none, never the JSON text `null`. */
const json = (value) => value === null ? null : JSON.stringify(value);
function parse(schema, value, message) {
    const parsed = schema.safeParse(value);
    if (!parsed.success)
        throw new BlogRefusal(message);
    return parsed.data;
}
function tagPath(settings, tagSlug) { return blogModule.tagAddress(settings.base, tagSlug); }
function postPath(settings, postSlug) { return blogModule.address(settings.base, postSlug); }
/**
 * Every write the blog has, each one transaction on the site's row (so the owner and Stax serialize), raising
 * `builder_blog.version` when what visitors see can change. Publishing, scheduling and updating run the same
 * paid-publishing policy as the Website's Publish.
 */
export class BlogService {
    db;
    policy;
    constructor(db, policy) {
        this.db = db;
        this.policy = policy;
    }
    /** The blog's state; refused when the site lacks the blog feature. */
    async read(siteId) {
        if (!hasFeature(await loadSiteFeatures(this.db.pool, siteId), "blog"))
            throw new BlogUnavailable();
        return new BlogStore(this.db.pool).load(siteId);
    }
    /** Every write runs here: the site's row, locked, and the blog feature checked on it before anything else happens. */
    inSite(siteId, actor, work) {
        return this.db.withSite(siteId, actor, async (client, site) => {
            if (!hasFeature(site, "blog"))
                throw new BlogUnavailable();
            return work(client, site);
        });
    }
    // ---- settings
    /** Creates the blog the first time, then renames it, moves its address or turns it on or off. An address a Website page uses is refused. */
    async saveSettings(siteId, actor, unknownInput) {
        const input = parse(BlogSettingsInputSchema, unknownInput, "The blog's name or address isn't valid");
        return this.inSite(siteId, actor, async (client, site) => {
            const found = await client.query("SELECT settings FROM builder_blog WHERE site_id=$1", [siteId]);
            const previous = found.rows[0] ? settingsFromJson(found.rows[0].settings) : null;
            if (!previous && !input.enabled)
                throw new BlogRefusal("The blog is not enabled");
            if (input.base !== previous?.base) {
                // The stored routes still name the blog by its old address after a move, so ask about the routes as they are now.
                const { manifest } = await this.db.loadBundleSnapshot(siteId, site.draftId, client);
                const routes = previous ? effectiveBlogRoutes(manifest.routes, { modules: { blog: { name: previous.name, base: previous.base, ...(previous.enabled ? {} : { enabled: false }) } } }) : manifest.routes;
                const shop = await client.query("SELECT settings FROM builder_shop WHERE site_id=$1", [siteId]);
                if (shop.rows[0]?.settings.base === input.base)
                    throw new BlogRefusal("The shop already uses that address");
                if (Object.hasOwn(routes, input.base))
                    throw new BlogRefusal("A page already uses that address");
            }
            const settings = { name: input.name, base: input.base, enabled: input.enabled, addToMenu: input.addToMenu ?? previous?.addToMenu ?? false, latestOnHome: input.latestOnHome ?? previous?.latestOnHome ?? false };
            await client.query(`INSERT INTO builder_blog(site_id,settings) VALUES($1,$2::jsonb)
        ON CONFLICT (site_id) DO UPDATE SET settings=EXCLUDED.settings, version=builder_blog.version+1, updated_at=now()`, [siteId, JSON.stringify(settings)]);
            let redirects = await this.redirects(client, siteId);
            if (settings.enabled)
                redirects = freeRedirect(redirects, settings.base);
            if (previous && previous.base !== settings.base)
                redirects = moveRedirect(redirects, previous.base, settings.base, true);
            await this.saveRedirects(client, siteId, redirects);
            return settings;
        });
    }
    // ---- tags
    async createTag(siteId, actor, input) {
        const parsed = parse(z.object({ id: identifier, label: z.string().trim().min(1).max(80), slug: slug.max(80), parentId: identifier.optional() }).strict(), input, "The tag isn't valid");
        return this.inSite(siteId, actor, async (client) => {
            const settings = await this.settings(client, siteId);
            if ((await client.query("SELECT 1 FROM builder_blog_tags WHERE site_id=$1 AND id=$2", [siteId, parsed.id])).rowCount)
                throw new BlogRefusal("Tag id already exists");
            await this.assertTagSlug(client, siteId, parsed.slug);
            if (parsed.parentId && !(await client.query("SELECT 1 FROM builder_blog_tags WHERE site_id=$1 AND id=$2", [siteId, parsed.parentId])).rowCount)
                throw new BlogRefusal("That parent tag doesn't exist");
            const position = (await client.query("SELECT coalesce(max(position),0)+1 AS next FROM builder_blog_tags WHERE site_id=$1", [siteId])).rows[0].next;
            await client.query("INSERT INTO builder_blog_tags(site_id,id,label,slug,parent_id,position) VALUES($1,$2,$3,$4,$5,$6)", [siteId, parsed.id, parsed.label, parsed.slug, parsed.parentId ?? null, position]);
            await this.saveRedirects(client, siteId, freeRedirect(await this.redirects(client, siteId), tagPath(settings, parsed.slug)));
            await this.bump(client, siteId);
            return { id: parsed.id, label: parsed.label, slug: parsed.slug, ...(parsed.parentId ? { parentId: parsed.parentId } : {}), position };
        });
    }
    async updateTag(siteId, actor, input) {
        const parsed = parse(z.object({ id: identifier, label: z.string().trim().min(1).max(80).optional(), slug: slug.max(80).optional(), parentId: identifier.nullable().optional() }).strict()
            .refine((value) => value.label !== undefined || value.slug !== undefined || value.parentId !== undefined), input, "The tag isn't valid");
        return this.inSite(siteId, actor, async (client) => {
            const settings = await this.settings(client, siteId);
            const found = await client.query("SELECT * FROM builder_blog_tags WHERE site_id=$1 AND id=$2", [siteId, parsed.id]);
            const tag = found.rows[0];
            if (!tag)
                throw new BlogRefusal("Tag does not exist");
            const nextSlug = parsed.slug ?? tag.slug;
            if (nextSlug !== tag.slug)
                await this.assertTagSlug(client, siteId, nextSlug);
            const parentId = parsed.parentId === undefined ? tag.parent_id : parsed.parentId;
            if (parentId && (parentId === tag.id || !(await client.query("SELECT 1 FROM builder_blog_tags WHERE site_id=$1 AND id=$2", [siteId, parentId])).rowCount))
                throw new BlogRefusal("That parent tag isn't valid");
            const label = parsed.label ?? tag.label;
            await client.query("UPDATE builder_blog_tags SET label=$3,slug=$4,parent_id=$5 WHERE site_id=$1 AND id=$2", [siteId, tag.id, label, nextSlug, parentId]);
            let redirects = freeRedirect(await this.redirects(client, siteId), tagPath(settings, nextSlug));
            if (nextSlug !== tag.slug)
                redirects = moveRedirect(redirects, tagPath(settings, tag.slug), tagPath(settings, nextSlug));
            await this.saveRedirects(client, siteId, redirects);
            await this.bump(client, siteId);
            return { id: tag.id, label, slug: nextSlug, ...(parentId ? { parentId } : {}), position: tag.position };
        });
    }
    /** Removes the tag from every post's working and live tags. */
    async deleteTag(siteId, actor, id) {
        await this.inSite(siteId, actor, async (client) => {
            await this.settings(client, siteId);
            const removed = await client.query("DELETE FROM builder_blog_tags WHERE site_id=$1 AND id=$2", [siteId, id]);
            if (!removed.rowCount)
                throw new BlogRefusal("Tag does not exist");
            await client.query("UPDATE builder_blog_tags SET parent_id=NULL WHERE site_id=$1 AND parent_id=$2", [siteId, id]);
            const posts = await client.query("SELECT id,working,live FROM builder_posts WHERE site_id=$1 AND (working->'tags' ? $2 OR live->'tags' ? $2) FOR UPDATE", [siteId, id]);
            for (const post of posts.rows) {
                const without = (copy) => copy ? { ...copy, tags: copy.tags.filter((tag) => tag !== id) } : null;
                await client.query("UPDATE builder_posts SET working=$3::jsonb, live=$4::jsonb, row_version=row_version+1, updated_at=now() WHERE site_id=$1 AND id=$2", [siteId, post.id, JSON.stringify(without(post.working)), json(without(post.live))]);
            }
            await this.bump(client, siteId);
        });
    }
    // ---- posts
    async createPost(siteId, actor, input, action = "create") {
        const id = parse(identifier, input.id, "The post id isn't valid");
        const working = parse(PostCopySchema, input.working, "The post isn't valid");
        return this.inSite(siteId, actor, async (client) => {
            await this.settings(client, siteId);
            if ((await client.query("SELECT 1 FROM builder_posts WHERE site_id=$1 AND id=$2", [siteId, id])).rowCount)
                throw new BlogRefusal("Post id already exists");
            await this.assertPostCopy(client, siteId, id, working);
            const inserted = await client.query("INSERT INTO builder_posts(site_id,id,working) VALUES($1,$2,$3::jsonb) RETURNING *", [siteId, id, JSON.stringify(working)]);
            await this.log(client, siteId, id, action, actor, null, working);
            return postFromRow(inserted.rows[0]);
        });
    }
    /** Saves the post's working copy (autosave). Readers are unaffected until the owner chooses Update post. A change that changes nothing isn't a change. */
    async savePost(siteId, actor, write, change) {
        return this.inSite(siteId, actor, async (client) => {
            const post = await this.lock(client, siteId, write);
            const next = this.merge(post.working, change);
            if (samePostCopy(next, post.working))
                return post;
            await this.assertPostCopy(client, siteId, post.id, next);
            const saved = await this.write(client, siteId, post, { working: next });
            await this.log(client, siteId, post.id, write.action ?? "edit", actor, post.working, next);
            return saved;
        });
    }
    /** Makes a draft post live. A date still ahead is Scheduling; use `schedulePost`. */
    async publishPost(siteId, actor, write) {
        return this.goLive(siteId, actor, write, "publish");
    }
    /** Makes a draft post live at the future time in its `publishAt`. */
    async schedulePost(siteId, actor, write) {
        return this.goLive(siteId, actor, write, "schedule");
    }
    /** Copies a published or scheduled post's working copy to what readers see. */
    async updatePost(siteId, actor, write) {
        return this.goLive(siteId, actor, write, "update");
    }
    async unpublishPost(siteId, actor, write) {
        return this.inSite(siteId, actor, async (client) => {
            const post = await this.lock(client, siteId, write);
            if (!post.live)
                throw new BlogRefusal("This post isn't published");
            const saved = await this.write(client, siteId, post, { live: null });
            await this.bump(client, siteId);
            return saved;
        });
    }
    async deletePost(siteId, actor, write) {
        await this.inSite(siteId, actor, async (client) => {
            const post = await this.lock(client, siteId, write);
            await client.query("DELETE FROM builder_posts WHERE site_id=$1 AND id=$2", [siteId, post.id]);
            await this.log(client, siteId, post.id, "delete", actor, post.working, null);
            if (post.live)
                await this.bump(client, siteId);
        });
    }
    /** Undo and Redo only restore the working copy; readers keep seeing the published version. */
    async undoPost(siteId, actor, write) {
        return this.restorePost(siteId, actor, write, "undo");
    }
    async redoPost(siteId, actor, write) {
        return this.restorePost(siteId, actor, write, "redo");
    }
    async restorePost(siteId, actor, write, action) {
        return this.inSite(siteId, actor, async (client) => {
            const post = await this.lock(client, siteId, write);
            const rows = await client.query("SELECT seq,action,actor,at,before,after,undoes FROM builder_post_changes WHERE site_id=$1 AND post_id=$2 ORDER BY seq", [siteId, post.id]);
            const history = postHistory(rows.rows);
            const copy = action === "undo" ? history.undo.at(-1)?.before : history.redo?.before;
            if (!copy)
                throw new BlogRefusal(`There's nothing to ${action}`);
            const known = await client.query("SELECT id FROM builder_blog_tags WHERE site_id=$1", [siteId]);
            const tags = new Set(known.rows.map(row => row.id));
            const restored = { ...copy, tags: copy.tags.filter(id => tags.has(id)) };
            if (samePostCopy(restored, post.working))
                throw new BlogRefusal(`There's nothing to ${action}`);
            await this.assertPostCopy(client, siteId, post.id, restored);
            const saved = await this.write(client, siteId, post, { working: restored });
            await this.log(client, siteId, post.id, action, actor, post.working, restored, action === "undo" ? history.undo.map(row => row.seq) : [history.redo.seq]);
            return saved;
        });
    }
    async postHistoryAvailability(siteId, postId) {
        if (!hasFeature(await loadSiteFeatures(this.db.pool, siteId), "blog"))
            return new Map();
        const rows = await this.db.pool.query(`SELECT c.post_id,c.seq,c.action,c.actor,c.at,c.before,c.after,c.undoes FROM builder_post_changes c
       WHERE c.site_id=$1 AND ($2::text IS NULL OR c.post_id=$2)
       AND EXISTS (SELECT 1 FROM builder_posts p WHERE p.site_id=c.site_id AND p.id=c.post_id) ORDER BY c.post_id,c.seq`, [siteId, postId ?? null]);
        const groups = new Map();
        for (const row of rows.rows) {
            const group = groups.get(row.post_id) ?? [];
            group.push(row);
            groups.set(row.post_id, group);
        }
        return new Map([...groups].map(([id, changes]) => {
            const history = postHistory(changes);
            return [id, { undoable: history.undo.length > 0, redoable: Boolean(history.redo) }];
        }));
    }
    async undoablePosts(siteId, postId) {
        return new Set([...(await this.postHistoryAvailability(siteId, postId))].filter(([, state]) => state.undoable).map(([id]) => id));
    }
    // ---- internals
    async goLive(siteId, actor, write, kind) {
        return this.inSite(siteId, actor, async (client, site) => {
            const post = await this.lock(client, siteId, write);
            const settings = await this.settings(client, siteId);
            const working = post.working;
            const future = working.publishAt !== undefined && Date.parse(working.publishAt) > Date.now();
            if (kind === "update") {
                if (!post.live)
                    throw new BlogRefusal("This post isn't published yet");
                if (samePostCopy(working, post.live))
                    return post;
                const live = post.live;
                if (future && !(live.publishAt && Date.parse(live.publishAt) > Date.now()))
                    throw new BlogRefusal("This post is already live. Remove its date, or unpublish it first, to schedule it.");
            }
            else {
                if (post.live)
                    throw new BlogRefusal("This post is already published. Choose Update post to share your changes.");
                if (kind === "publish" && future)
                    throw new BlogRefusal("This post has a date in the future. Schedule it instead.");
                if (kind === "schedule" && !future)
                    throw new BlogRefusal("Choose a date in the future to schedule this post.");
            }
            await this.policy?.(client, site, actor);
            const first = post.firstPublishedAt ?? (working.publishAt ? null : new Date().toISOString());
            const saved = await this.write(client, siteId, post, { live: working, firstPublishedAt: first });
            let redirects = freeRedirect(await this.redirects(client, siteId), postPath(settings, working.slug));
            if (post.live && post.live.slug !== working.slug)
                redirects = moveRedirect(redirects, postPath(settings, post.live.slug), postPath(settings, working.slug));
            await this.saveRedirects(client, siteId, redirects);
            await this.bump(client, siteId);
            return saved;
        });
    }
    merge(current, change) {
        const next = { ...current, ...change };
        for (const key of ["summary", "cover", "publishAt"])
            if (next[key] === null || next[key] === undefined)
                delete next[key];
        return parse(PostCopySchema, next, "The post isn't valid");
    }
    async settings(client, siteId) {
        const found = await client.query("SELECT settings FROM builder_blog WHERE site_id=$1", [siteId]);
        if (!found.rows[0])
            throw new BlogRefusal("Add a blog before editing its posts and tags");
        return settingsFromJson(found.rows[0].settings);
    }
    async lock(client, siteId, write) {
        await this.settings(client, siteId);
        const found = await client.query("SELECT * FROM builder_posts WHERE site_id=$1 AND id=$2 FOR UPDATE", [siteId, write.id]);
        if (!found.rows[0])
            throw new BlogRefusal("Post does not exist");
        const post = postFromRow(found.rows[0]);
        if (write.rowVersion !== undefined && write.rowVersion !== post.rowVersion)
            throw new BlogConflict("This post changed somewhere else. Reload it and try again.");
        if (write.expectedRevision !== undefined && write.expectedRevision !== post.working.revision)
            throw new BlogConflict("The post changed since you asked. Your words were kept.");
        return post;
    }
    async write(client, siteId, post, next) {
        const updated = await client.query(`UPDATE builder_posts SET working=$3::jsonb, live=$4::jsonb, first_published_at=$5, row_version=row_version+1, updated_at=now()
      WHERE site_id=$1 AND id=$2 RETURNING *`, [siteId, post.id, JSON.stringify(next.working ?? post.working),
            json(next.live === undefined ? post.live : next.live), next.firstPublishedAt === undefined ? post.firstPublishedAt : next.firstPublishedAt]);
        return postFromRow(updated.rows[0]);
    }
    async log(client, siteId, postId, action, actor, before, after, undoes) {
        await client.query(`INSERT INTO builder_post_changes(site_id,post_id,seq,action,actor,before,after,undoes)
      VALUES($1,$2,(SELECT coalesce(max(seq),0)+1 FROM builder_post_changes WHERE site_id=$1 AND post_id=$2),$3,$4,$5::jsonb,$6::jsonb,$7)`, [siteId, postId, action.slice(0, 40), actor.userId, before ? JSON.stringify(before) : null, after ? JSON.stringify(after) : null, undoes ?? null]);
    }
    async bump(client, siteId) {
        await client.query("UPDATE builder_blog SET version=version+1, updated_at=now() WHERE site_id=$1", [siteId]);
    }
    async redirects(client, siteId) {
        const rows = await client.query("SELECT from_path,to_path,prefix FROM builder_blog_redirects WHERE site_id=$1 ORDER BY from_path,prefix", [siteId]);
        return rows.rows.map((row) => ({ from: row.from_path, to: row.to_path, ...(row.prefix ? { prefix: true } : {}) }));
    }
    async saveRedirects(client, siteId, entries) {
        if (JSON.stringify(entries) === JSON.stringify(await this.redirects(client, siteId)))
            return;
        await client.query("DELETE FROM builder_blog_redirects WHERE site_id=$1", [siteId]);
        if (entries.length)
            await client.query("INSERT INTO builder_blog_redirects(site_id,from_path,to_path,prefix) SELECT $1,* FROM unnest($2::text[],$3::text[],$4::boolean[])", [siteId, entries.map((entry) => entry.from), entries.map((entry) => entry.to), entries.map((entry) => Boolean(entry.prefix))]);
    }
    async assertTagSlug(client, siteId, tagSlug) {
        if ((await client.query("SELECT 1 FROM builder_blog_tags WHERE site_id=$1 AND slug=$2", [siteId, tagSlug])).rowCount)
            throw new BlogRefusal("Another tag already uses that address");
    }
    /** A post's slug is unique across the working and live slugs of the other posts; its tags, cover and words must exist. */
    async assertPostCopy(client, siteId, postId, copy) {
        if ((await client.query("SELECT 1 FROM builder_posts WHERE site_id=$1 AND id<>$2 AND (working->>'slug'=$3 OR live->>'slug'=$3)", [siteId, postId, copy.slug])).rowCount)
            throw new BlogRefusal("Another post already uses that address");
        if (new Set(copy.tags).size !== copy.tags.length)
            throw new BlogRefusal("A tag is listed twice");
        if (copy.tags.length) {
            const known = await client.query("SELECT 1 FROM builder_blog_tags WHERE site_id=$1 AND id = ANY($2::text[])", [siteId, copy.tags]);
            if (known.rowCount !== copy.tags.length)
                throw new BlogRefusal("That tag doesn't exist");
        }
        if (!(await client.query("SELECT 1 FROM builder_post_revisions WHERE site_id=$1 AND digest=$2", [siteId, copy.revision])).rowCount)
            throw new BlogRefusal("Save this post's words before changing the post.");
        if (copy.cover && !(await client.query("SELECT 1 FROM builder_post_assets WHERE site_id=$1 AND asset_id=$2 AND version=$3", [siteId, copy.cover.assetId, copy.cover.version])).rowCount)
            throw new BlogRefusal("That photo is not available for posts. Add it from the post.");
    }
}
